import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import Search from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import {
  GetOrgMembersMemberResponseDto,
  JobDtoUser,
  OrganizationsService,
  OrganizationTeamResponseDto,
  OrganizationTeamWithMembersResponseDto,
  WorkspacesService,
} from 'app/API';
import { debounce } from 'lodash-es';
import React, { useEffect } from 'react';
import TeamEmptyWarningModal from './components/TeamsEmptyWarningModal';
import TeamsFilter from './components/TeamsFilter';
import TeamTag from './components/TeamTag';
import { Content } from './GetAssigneeWithoutCommentModal/styles/Content';

type TProps = {
  initialValue: GetOrgMembersMemberResponseDto | JobDtoUser | null;
  onCancel: () => void;
  onSuccess: (assignee: GetOrgMembersMemberResponseDto | JobDtoUser | null) => void;
};

export const GetAssigneeWithoutCommentModal = ({ initialValue, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { organization, workspace } = useAuthContext();

  const [users, setUsers] = React.useState<GetOrgMembersMemberResponseDto[]>([]);
  const [search, setSearch] = React.useState('');
  const [assignee, setAssignee] = React.useState<GetOrgMembersMemberResponseDto | JobDtoUser | null>(initialValue);

  const [chosenTeamsFilter, setChosenTeamsFilter] = React.useState<string[]>([]);
  const [teamsFilterValues, setTeamsFilterValues] = React.useState<
    (OrganizationTeamResponseDto | OrganizationTeamWithMembersResponseDto)[]
  >([]);
  const [isFetchingData, setIsFetchingData] = React.useState<boolean>(false);

  const userHasManageMembersPermissionForCurrentWorkspace = (organization?.userTeams ?? []).some(
    (userTeam) =>
      userTeam.manageMembers && (workspace?.Teams ?? []).some((workspaceTeam) => workspaceTeam.slug === userTeam.slug),
  );

  useEffect(() => {
    setIsFetchingData(true);

    const debouncedSearch = debounce(() => {
      if (userHasManageMembersPermissionForCurrentWorkspace) {
        call(
          OrganizationsService.getOrgMembers({
            oid: String(organization?.id),
            pageNo: '1',
            pageSize: '20',
            orderBy: '',
            orderType: '',
            search,
            teamsSlugFilter: chosenTeamsFilter.join('|'),
          }),
        )
          .then((result) => setUsers(result.data))
          .finally(() => setIsFetchingData(false));
        return;
      }

      call(
        OrganizationsService.getWorkspaceMembers({
          oid: String(organization?.id),
          workspaceId: String(workspace?.id),
          search,
          teamsSlugFilter: chosenTeamsFilter.join('|'),
        }),
      )
        .then((result) => setUsers(result))
        .finally(() => setIsFetchingData(false));
    }, 500);

    debouncedSearch();

    return () => debouncedSearch.cancel();
  }, [search, chosenTeamsFilter, userHasManageMembersPermissionForCurrentWorkspace]);

  useEffect(() => {
    setIsFetchingData(true);
    const getTeams = () => {
      if (!organization?.slug || !workspace?.slug) {
        return;
      }

      if (userHasManageMembersPermissionForCurrentWorkspace) {
        call(OrganizationsService.getOrgTeams({ oid: organization.id }))
          .then((response) => setTeamsFilterValues(response))
          .finally(() => setIsFetchingData(false));
        return;
      }

      call(WorkspacesService.getTeams({ orgSlug: organization?.slug, wsSlug: workspace?.slug }))
        .then((response) => setTeamsFilterValues(response.result))
        .finally(() => setIsFetchingData(false));
    };

    getTeams();
  }, [organization, workspace, userHasManageMembersPermissionForCurrentWorkspace]);

  const changeAssignee = (data: GetOrgMembersMemberResponseDto | null) => {
    if (!data) {
      setAssignee(null);
      return;
    }
    setAssignee(data);
  };

  const renderAssignee = () => {
    if (!assignee) {
      return;
    }
    return (
      <div className="assignTo">
        <div className="assignToText">
          <p>
            {assignee.name || ''} {assignee.lastname || ''}
          </p>
          {assignee.email ? <p>{assignee.email}</p> : ''}
        </div>
        <IconButtonDelete type="button" title="Clear assignee" onClick={() => changeAssignee(null)}>
          <TrashBin />
        </IconButtonDelete>
      </div>
    );
  };

  const handleSubmit = async () => {
    onSuccess(assignee);
  };

  return (
    <ModalWrapper width="600px">
      <ModalTitle>Set assignee</ModalTitle>
      <Content>
        {!teamsFilterValues.length && !isFetchingData ? (
          <TeamEmptyWarningModal
            linkToWorkspacesPage={`/${organization?.slug}/workspaces/${workspace?.slug}/details`}
            workspaceTitle={String(workspace?.name)}
          />
        ) : null}

        <p className="assignedTo">{assignee ? 'Currently assigned to' : 'Currently not assigned'}</p>
        {renderAssignee()}
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          label="Search user to assign"
          name="assignUser"
          icon={Search}
          value={search}
        />

        <TeamsFilter
          teamsFilterValues={teamsFilterValues}
          chosenTeamsFilter={chosenTeamsFilter}
          setChosenTeamsFilter={setChosenTeamsFilter}
          isOrganizationTeams={userHasManageMembersPermissionForCurrentWorkspace}
        />

        <TableWrapper className="customTable">
          {isFetchingData ? <AbsoluteSpinner overlay className="mt-4 h-full" /> : null}
          <Table>
            <Thead $white>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Teams</Th>
              </Tr>
            </Thead>
            {!isFetchingData ? (
              <Tbody>
                {users.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                        {data?.name}
                      </p>
                    </Td>
                    <Td>
                      <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                        {data?.email}
                      </p>
                    </Td>
                    <Td>
                      <div className="flex">
                        {(
                          data as GetOrgMembersMemberResponseDto & { teams?: { id: string; slug: string }[] }
                        )?.teams?.map((team) => (
                          <TeamTag
                            key={team.id}
                            team={team}
                            active={!chosenTeamsFilter.length || chosenTeamsFilter.includes(team.slug)}
                            onClick={() => changeAssignee(data)}
                          />
                        ))}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            ) : null}
          </Table>
        </TableWrapper>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button data-testid="saveButton" onClick={handleSubmit}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
